<template>
  <div class="grid-row">
    <div class="submission-rate-container grid-cell">
      <SubmissionRateChip :fill-rate="fillRate" />
    </div>
    <div class="req-name grid-cell">
      <DpplTooltip
        tooltip-placement="right"
        :tooltip-offset="8"
        class="req-name-tooltip"
      >
        <template #activator="{ onMouseenter, onMouseleave }">
          <p
            class="new-h3"
            @mouseenter="onMouseenter"
            @mouseleave="onMouseleave"
          >
            {{ title }}
          </p>
        </template>
        <template #tooltip-content>
          <p class="tooltip-text new-body-text">
            {{ title }}
          </p>
        </template>
      </DpplTooltip>
      <DpplTooltip
        tooltip-placement="top"
        :tooltip-offset="8"
      >
        <template #activator="{ onMouseenter, onMouseleave }">
          <router-link
            :to="{ name: 'requisition-report', params: { id: requisitionUlid } }"
            @mouseenter="onMouseenter"
            @mouseleave="onMouseleave"
            @click="handleReqReportClick"
          >
            <v-icon
              icon="$open-in-new"
              size="14px"
              class="ml-2"
              color="brandPrimary"
            />
          </router-link>
        </template>
        <template #tooltip-content>
          <p class="tooltip-text new-body-text">
            View Req Progress Report
          </p>
        </template>
      </DpplTooltip>
    </div>
    <div class="req-id grid-cell">
      <DpplTooltip
        tooltip-placement="top"
        :tooltip-offset="8"
      >
        <template #activator="{ onMouseenter, onMouseleave }">
          <p
            class="new-body-text"
            @mouseenter="onMouseenter"
            @mouseleave="onMouseleave"
          >
            {{ atsRequisitionLabel }}
          </p>
        </template>
        <template #tooltip-content>
          <p class="tooltip-text new-body-text">
            {{ atsRequisitionLabel }}
          </p>
        </template>
      </DpplTooltip>
    </div>
    <div class="department grid-cell">
      <DpplTooltip
        tooltip-placement="top"
        :tooltip-offset="8"
      >
        <template #activator="{ onMouseenter, onMouseleave }">
          <p
            class="new-body-text"
            @mouseenter="onMouseenter"
            @mouseleave="onMouseleave"
          >
            {{ department }}
          </p>
        </template>
        <template #tooltip-content>
          <p class="tooltip-text new-body-text">
            {{ department }}
          </p>
        </template>
      </DpplTooltip>
    </div>
    <div class="status-badge-container grid-cell">
      <span
        :class="{
          'status-badge': true,
          'new-body-text': true,
          open: status === 'open',
        }"
      >
        {{ formattedStatus }}
      </span>
    </div>
    <div class="grid-cell">
      <ATSUrl :url="atsUrl" @track-ats-click="handleAtsUrlClick" />
    </div>
  </div>
</template>

<script setup>
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import SubmissionRateChip from '@/components/insights/ProcessInsights/DemographicSurveyReport/SubmissionRateChip.vue';
import ATSUrl from '@/components/shared/ATSUrl.vue';
import { startCase } from 'lodash-es';
import { computed } from 'vue';
import { useTracking } from '@/stores/tracking/tracking';

const props = defineProps({
  atsRequisitionLabel: { type: String, default: '' },
  title: { type: String, required: true },
  department: { type: String, required: true },
  fillRate: { type: Number, required: true },
  status: { type: String, required: true },
  requisitionUlid: { type: String, required: true },
  atsUrl: { type: String, required: true },
});

const formattedStatus = computed(() => startCase(props.status));

const handleAtsUrlClick = (url) => {
  useTracking().track({
    name: 'Clicked ATS Requisition link on Demographic-survey-report page',
    params: {
      candidateUrl: url,
      requisitionUlid: props.requisitionUlid,
    },
  });
};

const handleReqReportClick = () => {
  useTracking().track({
    name: 'Clicked Req Report link on Demographic-survey-report page',
    params: {
      requisitionUlid: props.requisitionUlid,
    },
  });
};
</script>

<style lang="less" scoped>
.grid-row {
  display: grid;
  grid-template-columns: minmax(100px, 145px) minmax(175px, 1fr) minmax(70px, 95px) minmax(85px, 206px) minmax(75px, 100px) minmax(65px, 102px);
  width: 100%;
}

.grid-cell {
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  &:not(.req-name):not(.req-id):not(.department) {
    justify-content: center;
  }
}

.grid-row .grid-cell {
  padding: 13px 12px;
  color: @text-dark;

  &.req-name {
    vertical-align: middle;

    p {
      color: @text-dark;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &.department {
    width: 100%;

    p {
      color: @text-dark;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &.req-id p {
    color: @text-dark;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.submission-rate-container, &.status-badge-container {
    word-wrap: break-word;
  }

  &.req-settings {
    word-wrap: break-word;
  }
}

.status-badge {
  padding: 4px 8px;
  border-radius: 40px;
  background: fade(@grey-700, 8%);
  color: @text-light;

  &.open {
    background: fade(@navy-500, 10%);
    color: @navy-500;
  }
}

.tooltip-text {
  color: @text-darkest;
}

.req-name-tooltip {
  max-width: calc(100% - 20px);
  overflow: hidden;
}
</style>
